import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AuthError, useAuthServices } from '../authServices';
import { AuthLayout } from '../AuthLayout';
import { Icon } from '../../common/Icon';

/** QZ-D28: sign-up form, calling Cognito's sign-up directly through `useAuthServices()`. */
export const SignUpPage = () => {
  const { t } = useTranslation();
  const { cognito } = useAuthServices();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [mismatch, setMismatch] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setMismatch(password !== passwordRepeat);
    if (password !== passwordRepeat) {
      return;
    }
    setBusy(true);
    try {
      await cognito.signUp(email, password);
      navigate(`/signup/confirm?email=${encodeURIComponent(email)}`);
    } catch (thrown) {
      setError(
        thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.Unknown')
      );
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      footer={
        <>
          {t('auth.signUp.haveAccount')} <Link to="/signin">{t('auth.signUp.toSignIn')}</Link>
        </>
      }
    >
      <h1>{t('auth.signUp.heading')}</h1>
      <p className="qz-sub">{t('auth.signUp.allowList')}</p>
      {error && (
        <div className="alert alert-danger">
          <Icon name="circle-exclamation" />
          <div>{error}</div>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="signup-email" className="form-label">
            {t('auth.email')}
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="username"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="signup-password" className="form-label">
            {t('auth.password')}
          </label>
          <input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            className="form-control"
            aria-describedby="signup-password-rule"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* CDK's default pool policy; nothing in the infra overrides it (QZ-D18). */}
          <div id="signup-password-rule" className="form-text">
            {t('auth.signUp.passwordRule')}
          </div>
        </div>
        <div className="mb-3">
          <label htmlFor="signup-password-repeat" className="form-label">
            {t('auth.passwordRepeat')}
          </label>
          <input
            id="signup-password-repeat"
            type="password"
            autoComplete="new-password"
            className={`form-control${mismatch ? ' is-invalid' : ''}`}
            aria-describedby={mismatch ? 'signup-password-repeat-error' : undefined}
            value={passwordRepeat}
            onChange={(e) => setPasswordRepeat(e.target.value)}
          />
          {mismatch && (
            <div id="signup-password-repeat-error" className="invalid-feedback d-block">
              {t('auth.signUp.passwordMismatch')}
            </div>
          )}
        </div>
        <button type="submit" className="btn btn-primary w-100" disabled={busy}>
          {t('auth.signUp.submit')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SignUpPage;
